import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import { setKioskToken, testConnection } from '../visitorKioskApi';

// Fontana brand color
const FONTANA_BLUE = '#1a365d';

interface KioskSetupProps {
  onSetupComplete: () => void;
}

export default function KioskSetup({ onSetupComplete }: KioskSetupProps) {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedToken = token.trim();
    if (trimmedToken.length < 10) {
      setError('Token måste vara minst 10 tecken.');
      return;
    }

    setLoading(true);
    setError(null);

    setKioskToken(trimmedToken);
    
    const isValid = await testConnection();
    
    if (isValid) {
      onSetupComplete();
    } else {
      setError('Ogiltig token. Kontrollera att du klistrat in rätt värde.');
    }
    
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: FONTANA_BLUE,
      }}
    >
      {/* Header */}
      <Box sx={{ py: 2, px: 4 }}>
        <Box
          component="img"
          src="/Fontana_Logo_Neg.png"
          alt="Fontana"
          sx={{ height: 36, width: 'auto' }}
        />
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <KeyIcon sx={{ fontSize: 48, color: FONTANA_BLUE, mb: 1.5 }} />
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Kiosk-konfiguration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ange kiosk-token för att aktivera besökshanteringen
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Kiosk-token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                type="password"
                placeholder="Klistra in token..."
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': { minHeight: 52, borderRadius: 2 },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || token.trim().length === 0}
                sx={{ 
                  minHeight: 52, 
                  fontSize: '1rem', 
                  borderRadius: 2,
                  bgcolor: FONTANA_BLUE,
                  '&:hover': { bgcolor: '#0f2744' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Aktivera kiosk'}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
              Kontakta IT-support om du inte har en token
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Besökshantering
        </Typography>
      </Box>
    </Box>
  );
}
